import { ReactNode, useMemo, useRef } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useListCustomers } from "@/lib/api/customer";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "@/lib/utils";

interface Customer {
  id: string;
  long_name?: string;
  short_name?: string;
  hasRiskProfile?: boolean;
}

export interface CustomerMultiPickerProps {
  relationType: "risk" | "freight" | "weight_ticket";
  /** Header title shown in the collapsed/expanded card (e.g. "Risk Profiles"). */
  title: string;
  /** Optional icon shown to the left of the title. */
  icon?: ReactNode;
  /** Optional small description shown under the title when expanded. */
  description?: string;
  selectedCustomerIds: string[];
  onChange: (next: string[]) => void;
  searchQuery: string;
  onSearchQueryChange: (next: string) => void;
  /** Controlled collapsed/expanded state. */
  isOpen: boolean;
  onOpenChange: (next: boolean) => void;
  maxAllowed?: number;
  disabled?: boolean;
  /** Optional footer rendered inside the expanded content (e.g. Save/Reset buttons). */
  footer?: ReactNode;
}

/**
 * Unified collapsible customer multi-picker.
 *
 * - Header (collapsed or expanded) always shows the current selection as a
 *   single-line, comma-separated, truncated list of customer names. This
 *   reflects the in-flight (unsaved) selection — never a persisted snapshot.
 * - When expanded, renders the search input + virtualized selected/available
 *   lists. Fully controlled: parent owns the selection state, search state,
 *   and open/closed state so it can be driven by a useReducer (e.g.
 *   OnboardUserModal) or a thin local-state wrapper.
 */
export function CustomerMultiPicker({
  relationType,
  title,
  icon,
  description,
  selectedCustomerIds,
  onChange,
  searchQuery,
  onSearchQueryChange,
  isOpen,
  onOpenChange,
  maxAllowed,
  disabled = false,
  footer,
}: CustomerMultiPickerProps) {
  const selectedListRef = useRef<HTMLDivElement>(null);
  const availableListRef = useRef<HTMLDivElement>(null);

  const customersRes = useListCustomers({ minimal: true });
  const customers = (customersRes?.data?.data || []) as Customer[];

  const selectedIdSet = useMemo(() => new Set(selectedCustomerIds), [selectedCustomerIds]);

  const displayName = (customer: Customer) =>
    customer.long_name || customer.short_name || "Unknown";

  const selectedCustomers = useMemo(() => {
    return customers.filter((customer: Customer) => selectedIdSet.has(customer.id));
  }, [customers, selectedIdSet]);

  // Header label reflects the live, unsaved selection. Comma-joined and
  // truncated by CSS in the header layout below.
  const headerSelectionLabel = useMemo(() => {
    if (selectedCustomers.length === 0) return "None selected";
    return selectedCustomers.map((customer: Customer) => displayName(customer)).join(", ");
  }, [selectedCustomers]);

  const availableCustomers = useMemo(() => {
    let result = customers.filter((customer: Customer) => !selectedIdSet.has(customer.id));

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter((customer: Customer) =>
        customer.long_name?.toLowerCase().includes(query)
      );
    }

    if (relationType === "risk") {
      result = result.filter((customer: Customer) => customer.hasRiskProfile);
    }

    return result;
  }, [customers, selectedIdSet, searchQuery, relationType]);

  const selectedVirtualizer = useVirtualizer({
    count: selectedCustomers.length,
    getScrollElement: () => selectedListRef.current,
    estimateSize: () => 36,
    overscan: 10,
  });

  const availableVirtualizer = useVirtualizer({
    count: availableCustomers.length,
    getScrollElement: () => availableListRef.current,
    estimateSize: () => 36,
    overscan: 30,
  });

  const handleCustomerToggle = (customerId: string) => {
    if (disabled) return;
    const isCurrentlySelected = selectedIdSet.has(customerId);
    if (isCurrentlySelected) {
      onChange(selectedCustomerIds.filter((id: string) => id !== customerId));
      return;
    }
    if (maxAllowed === 1) {
      onChange([customerId]);
      return;
    }
    onChange([...selectedCustomerIds, customerId]);
  };

  const renderCustomerCard = (
    customer: Customer,
    virtualItem: { index: number; start: number },
    virtualizer: { measureElement: (el: Element | null) => void },
    isSelected: boolean
  ) => {
    return (
      <div
        key={customer.id}
        data-index={virtualItem.index}
        ref={virtualizer.measureElement}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          transform: `translateY(${virtualItem.start}px)`,
        }}
      >
        <div
          onClick={() => handleCustomerToggle(customer.id)}
          className={cn(
            "px-3 py-1.5 cursor-pointer transition-colors border-b border-gray-100 last:border-b-0",
            isSelected
              ? "bg-blue-50 border-blue-200 hover:bg-blue-100"
              : "hover:bg-gray-50",
            disabled && "opacity-50 cursor-not-allowed"
          )}
          style={{ minHeight: "32px" }}
        >
          <span
            className={cn(
              "text-sm",
              isSelected ? "font-medium text-blue-900" : "text-gray-900"
            )}
          >
            {displayName(customer)}
          </span>
        </div>
      </div>
    );
  };

  const selectedVirtualItems = selectedVirtualizer.getVirtualItems();
  const availableVirtualItems = availableVirtualizer.getVirtualItems();
  const isMaxExceeded = maxAllowed !== undefined && selectedCustomers.length > maxAllowed;

  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <CollapsibleTrigger className="w-full" disabled={disabled}>
        <div
          className={cn(
            "flex items-center gap-3 p-3 rounded-lg border transition-colors",
            isOpen
              ? "bg-gray-50 border-gray-300"
              : "bg-white border-gray-200 hover:bg-gray-50",
            disabled && "opacity-60 cursor-not-allowed"
          )}
        >
          {/* Chevron + icon + title block — fixed size on the left. */}
          <div className="flex items-center gap-3 flex-shrink-0">
            {isOpen ? (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-500" />
            )}
            {icon && <span className="text-gray-600">{icon}</span>}
            <span className="font-medium whitespace-nowrap">{title}</span>
          </div>

          {/* Selection label — flex-1 so it absorbs the remaining width and
              keeps a gap from the title. truncate handles overflow. */}
          <span
            className={cn(
              "flex-1 min-w-0 text-sm text-gray-500 text-right truncate",
              selectedCustomers.length === 0 && "italic"
            )}
            title={headerSelectionLabel}
          >
            {headerSelectionLabel}
          </span>
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="mt-2 p-4 border rounded-lg bg-white">
          <div className="flex flex-col space-y-3 min-h-0">
            {description && (
              <p className="text-xs text-gray-500 flex-shrink-0">{description}</p>
            )}

            <div className="space-y-2 flex-shrink-0">
              <Label htmlFor={`search-${relationType}`}>Search Customers</Label>
              <Input
                id={`search-${relationType}`}
                value={searchQuery}
                onChange={(e) => onSearchQueryChange(e.target.value)}
                placeholder="Search by customer name..."
                disabled={disabled}
              />
            </div>

            {selectedCustomers.length > 0 && (
              <div className="flex flex-col space-y-2 flex-shrink-0">
                <Label className="text-sm font-medium text-gray-700">
                  Selected ({selectedCustomers.length}
                  {maxAllowed !== undefined ? ` / ${maxAllowed}` : ""})
                  {isMaxExceeded && (
                    <span className="ml-2 text-xs text-red-600 font-normal">
                      Maximum exceeded
                    </span>
                  )}
                </Label>
                <div
                  ref={selectedListRef}
                  className="overflow-y-auto border rounded-lg bg-blue-50/30"
                  style={{ maxHeight: "160px", minHeight: "80px" }}
                >
                  <div
                    style={{
                      height: `${selectedVirtualizer.getTotalSize()}px`,
                      width: "100%",
                      position: "relative",
                    }}
                  >
                    {selectedVirtualItems.map((virtualItem) => {
                      const customer = selectedCustomers[virtualItem.index];
                      return renderCustomerCard(
                        customer,
                        virtualItem,
                        selectedVirtualizer,
                        true
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col space-y-2 flex-1 min-h-0">
              <Label className="text-sm font-medium text-gray-700">
                Available ({availableCustomers.length})
              </Label>
              <div
                ref={availableListRef}
                className="flex-1 overflow-y-auto border rounded-lg"
                style={{ maxHeight: "260px", minHeight: "160px" }}
              >
                {availableCustomers.length === 0 ? (
                  <div className="flex items-center justify-center h-full py-8">
                    <p className="text-sm text-gray-500">
                      {searchQuery ? "No customers found" : "No customers available"}
                    </p>
                  </div>
                ) : (
                  <div
                    style={{
                      height: `${availableVirtualizer.getTotalSize()}px`,
                      width: "100%",
                      position: "relative",
                    }}
                  >
                    {availableVirtualItems.map((virtualItem) => {
                      const customer = availableCustomers[virtualItem.index];
                      return renderCustomerCard(
                        customer,
                        virtualItem,
                        availableVirtualizer,
                        false
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {footer}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
